import { Urls } from 'Common/Urls';
import { parseSpec } from 'Shared/posters/fonts/fonts';
import { UploadedFont, VenueFonts } from 'Shared/model/venueSettings';
import { fontWeightRange } from 'Shared/frontend/fonts/fontFaces';


export interface FontFamilyOption
{
	family: string;
	fontFamily: string;
}


export function googleFontHref(spec:string)
{
	return `https://fonts.googleapis.com/css2?family=${spec}&display=swap`;
}

export function cssFontFamily(family:string,fallback:string)
{
	return `"${family}", ${fallback}`;
}

export function uploadedFontFaceCss(urls:Urls,font:UploadedFont)
{
	const url = urls.fontUrl(font.hash,font.format,font.dev ?? false);
	const format = font.format == 'ttf' ? 'truetype' : font.format == 'otf' ? 'opentype' : font.format;

	return `@font-face {
	font-family: "${font.family}";
	src: url("${url}") format("${format}");
	font-weight: ${fontWeightRange(font)};
	font-style: ${font.style};
	font-display: swap;
}`;
}

/* Every family the venue loads, once each, in the order they were added. */
export function venueFontFamilies(fonts:VenueFonts): FontFamilyOption[]
{
	const all = [
		...fonts.google.map(font => ({family: parseSpec(font.spec).family, fallback: font.fallback})),
		...fonts.external,
		...fonts.uploaded,
	];

	const options: FontFamilyOption[] = [];
	for (const font of all)
		if (!options.some(option => option.family == font.family))
			options.push({family: font.family, fontFamily: cssFontFamily(font.family,font.fallback)});

	return options;
}

/* {css font-family: label} for a font dropdown: the venue's fonts, and the current value if it isn't one of them */
export function fontFamilyOptions(fonts:VenueFonts,current:string|undefined): Record<string,string>
{
	const options = Object.fromEntries(venueFontFamilies(fonts).map(font => [font.fontFamily,font.family]));

	return current == undefined || current in options ? options : {...options, [current]: current};
}

/*
	Import code from a font site other than Google, as css to go in a <style> tag. A <link> becomes
	an @import of its stylesheet, a <style> block loses its tags, and anything else is taken to be css
	already.
*/
export function parseExternalEmbed(text:string)
{
	const links = [...text.matchAll(/<link\b[^>]*\bhref\s*=\s*["']([^"']+)["'][^>]*>/gi)]
		.filter(match => !/\brel\s*=\s*["']?(preconnect|dns-prefetch)/i.test(match[0]))
		.map(match => match[1]);

	const styles = [...text.matchAll(/<style\b[^>]*>([\s\S]*?)<\/style>/gi)].map(match => match[1].trim());

	if (links.length == 0 && styles.length == 0)
		return text.trim();

	return [
		...links.map(href => `@import url("${href}");`),
		...styles,
	].join('\n');
}

/* The family names an @font-face block declares - used to suggest a name for pasted import code. */
export function declaredFontFamilies(css:string)
{
	return [...css.matchAll(/font-family\s*:\s*["']?([^"';}]+?)["']?\s*;/gi)].map(match => match[1].trim());
}
